import { useCallback, useEffect, useRef, useState } from "react";
import { Body } from "../../../components/main-body/body";
import { Button, Progress, Tag, Alert, Table, Space } from "antd";
import ApiSurveyReportBackfill from "../../../api/survey-report-backfill";
import ApiOnboardingReportBackfill from "../../../api/onboarding-report-backfill";

// Minimal systemAdmin control panel for the data-report answers backfill.
// Creates report-answer rows for already-completed surveys/onboardings that
// never produced them (bot / admin-on-behalf / pre-pipeline completions). The
// work runs in the backend; this page triggers it and polls status.
const POLL_MS = 2000;

const STATUS_COLOR = {
  idle: "default",
  running: "processing",
  done: "success",
  failed: "error",
};

const Stat = ({ label, value, color }) => (
  <div>
    <div style={{ fontSize: 12, color: "#6C6C89" }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 600, color }}>{value}</div>
  </div>
);

// One self-contained backfill block (controls + status + errors) driven by the
// given API. Reused for both the survey and the onboarding backfills.
const BackfillPanel = ({ api, description, idLabel, idField, unmatchedMessage }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.getStatus();
      setStatus(res.data.status);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to load status");
    }
  }, [api]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const handleStart = async () => {
    setLoading(true);
    try {
      await api.start();
      await fetchStatus();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to start");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    setLoading(true);
    try {
      await api.stop();
      await fetchStatus();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to stop");
    } finally {
      setLoading(false);
    }
  };

  const isRunning = status?.status === "running";
  const total = status?.total || 0;
  const processed = status?.processed || 0;
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

  const errorColumns = [
    { title: idLabel, dataIndex: idField, key: idField, width: 340 },
    { title: "Error", dataIndex: "message", key: "message" },
  ];

  return (
    <div style={{ maxWidth: 920 }}>
      <p style={{ color: "#6C6C89", marginBottom: 16 }}>{description}</p>

      {status?.unmatched > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={`${status.unmatched} completed item(s) had no report version whose schema matched their responses — these were NOT backfilled and need a look (${unmatchedMessage}).`}
        />
      )}

      {error && (
        <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />
      )}

      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" loading={loading} disabled={isRunning} onClick={handleStart}>
          {status?.status === "done" || status?.status === "failed" ? "Restart" : "Start"}
        </Button>
        <Button danger disabled={!isRunning} onClick={handleStop}>
          Stop
        </Button>
        <Button onClick={fetchStatus}>Refresh</Button>
      </Space>

      <div style={{ marginBottom: 12 }}>
        Status: <Tag color={STATUS_COLOR[status?.status] || "default"}>{status?.status || "—"}</Tag>
      </div>

      <Progress percent={percent} status={isRunning ? "active" : undefined} />

      <div style={{ display: "flex", gap: 24, margin: "16px 0", flexWrap: "wrap" }}>
        <Stat label="Total" value={total} />
        <Stat label="Processed" value={processed} />
        <Stat label="Created" value={status?.created || 0} />
        <Stat label="Skipped" value={status?.skipped || 0} />
        <Stat label="Unmatched" value={status?.unmatched || 0} color={status?.unmatched ? "#d46b08" : undefined} />
        <Stat label="Failed" value={status?.failed || 0} color={status?.failed ? "#cf1322" : undefined} />
      </div>

      <div style={{ color: "#6C6C89", fontSize: 13, marginBottom: 16 }}>
        {status?.startedAt && <div>Started: {status.startedAt}</div>}
        {status?.finishedAt && <div>Finished: {status.finishedAt}</div>}
        {status?.lastError && <div style={{ color: "#cf1322" }}>Last error: {status.lastError}</div>}
      </div>

      {status?.errors?.length > 0 && (
        <>
          <h4>Row errors ({status.errors.length})</h4>
          <Table
            size="small"
            rowKey={(r, i) => `${r[idField]}-${i}`}
            columns={errorColumns}
            dataSource={status.errors}
            pagination={false}
          />
        </>
      )}
    </div>
  );
};

const SurveyReportBackfill = () => {
  return (
    <Body header={{ title: "Data report backfill" }} scroll>
      <div style={{ padding: 16 }}>
        <Alert
          type="info"
          showIcon
          style={{ maxWidth: 920, marginBottom: 24 }}
          title="Progress is tracked in the backend instance that ran it. With multiple instances, open this from the same one; a restart clears the status (the backfill is idempotent, so re-running is safe)."
        />

        <h3 style={{ marginBottom: 12 }}>Survey reports</h3>
        <BackfillPanel
          api={ApiSurveyReportBackfill}
          idLabel="Subject Task ID"
          idField="subjectTaskId"
          unmatchedMessage="the survey questions likely changed between completion and now"
          description="Creates survey data-report rows for already-completed survey tasks that never produced them (bot / demo / admin-on-behalf completions, or completions from before data reports existed). Idempotent: tasks that already have rows are skipped, so it is safe to start again."
        />

        <div style={{ height: 1, background: "#ECECF1", margin: "32px 0", maxWidth: 920 }} />

        <h3 style={{ marginBottom: 12 }}>Onboarding reports</h3>
        <BackfillPanel
          api={ApiOnboardingReportBackfill}
          idLabel="Subject Onboarding ID"
          idField="subjectOnboardingSurveyId"
          unmatchedMessage="the onboarding questions likely changed between completion and now"
          description="Creates onboarding data-report rows for already-completed onboardings that never produced them (bot / admin-on-behalf completions, or completions from before data reports existed). Idempotent: onboardings that already have rows are skipped, so it is safe to start again."
        />
      </div>
    </Body>
  );
};

export default SurveyReportBackfill;
